import React, {useState} from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import {registerUser, connectionError, makeLogin} from './userSlice';
import useInputState from '../hooks/forms/useInputState';
import useCheckboxState from '../hooks/forms/useCheckboxState';
import {FormValidator, ShowValidation } from '../utils/validation/FormValidator';
import {EmailField, PasswordField, PasswordConfirmationField, TermsField} from '../utils/validation/auth/AuthValidators';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import ErrorMessage from '../elements/ErrorMessage';
import styles from '../styles/login/LoginForm.module.css';

const SignupForm = (props) => {
    const {redirectUrl, loginLink, defaultEmail} = props;
    const router = useRouter();
    const [isLoading, editIsLoading] = useState(false);
    const [email, editEmail, resetEmail] = useInputState(defaultEmail ? defaultEmail: '');
    const [password, editPassword, resetPassword] = useInputState("");
    const [passwordConfirmation, editPasswordConfirmation, resetPasswordConfirmation] = useInputState("");
    // const [terms, editTerms, resetTerms] = useInputState(false);
    const [isChecked, editIsChecked] = useCheckboxState('');
    const [isNewsletter, editIsNewsletter] = useState(false);
    const dispatch = useDispatch();
    let signupError = useSelector(state => state.user.error);


    //load validation rules for fields
    const ValidationRules = {
        email: EmailField,
        password: PasswordField,
        passwordConfirmation: {rule: PasswordConfirmationField, param: password},
        terms: TermsField
    };

    //blur validation
    function validateFieldOnBlur(e){
        const field = e.target;
        let value;
        if (field.type=='checkbox') {
            value = field.checked ? field.value : false;
        } else {
            value = field.value;
        }
        const id= e.target.id;
        
        let res;
        if (typeof ValidationRules[id] == 'object') {
            res = ValidationRules[id].rule(value, ValidationRules[id].param);
        } else {
            res = ValidationRules[id](value);
        }
        ShowValidation(id, res);
    }

    const handleChangeCheckbox = (e) => {
        editIsChecked(e);
        validateFieldOnBlur(e);
    }

    const signinUser = async (email, password) => {
        try {
            const resultAction = await dispatch(
                makeLogin({email: email, password: password})
                );
            const response = unwrapResult(resultAction);
         
            if (response.status == true) {
                if (redirectUrl) {
                    router.push(redirectUrl);
                }
            }
        }
         catch(err) {
            editIsLoading(false);
            dispatch(connectionError('Ups, nu ne putem conecta. Te rugam sa incerci mai tarziu.'));
        }
    }


    const handleSignup = async (e) => {
        e.preventDefault();
        let validForm = FormValidator('signupForm', ValidationRules);
        if (validForm) {
            editIsLoading(true);
            try{
                const resultAction = await dispatch(registerUser(
                    {
                        email: email,
                        password: password,
                        password_confirmation: passwordConfirmation,
                        // Consent capture — TOS + Privacy are required by the
                        // form validator; the backend re-checks them anyway.
                        consents: {
                            tos: true,
                            privacy_policy: true,
                            newsletter: isNewsletter,
                        },
                    }
                ));
                editIsLoading(false);
                const response = unwrapResult(resultAction);
                if (response.status == true) {
                    await signinUser(email, password);
                }

            } catch(err) {
                editIsLoading(false);
                dispatch(connectionError('Ups, avem o problemă. Te rugăm sa încerci mai târziu.'))
            }
        } 

    };

    const spinner = isLoading &&  
                    <>
                        <Spinner as="span" animation="grow" size="sm" role="status"
                                aria-hidden="true" />
                           Te rugăm să aștepți...
                    </>;
    const btnDisabled = isLoading && 'disabled';
    const signupBtn =  <Button variant="primary" type="submit" className="col-12" disabled={btnDisabled}  >
                        {spinner}
                        {!isLoading && 'Creează un cont'}
                    </Button>;
    const termsLink = <Link
        href={`${process.env.NEXT_PUBLIC_WEB}/termeni/generali`}
        title="Termeni Conso"
        target="_blank">
                        Termenii de utilizare
                    </Link>;
    const privacyLink =  <Link
        href={`${process.env.NEXT_PUBLIC_WEB}/termeni/date-personale`}
        title="Politica privind datele personale"
        target="_blank">
                            Politica de confidentialitate
                        </Link>;

    const termsLabel = <>Sunt de acord cu {termsLink} si {privacyLink}</> ;
    const errorMessage = (signupError && signupError.isArray) ? <ul>{signupError.map(e => <li>{e}</li>)}</ul> : signupError; 

    const errorBox = signupError && 
                            <div className="mb-3">
                                <ErrorMessage title="Eroare" message={errorMessage} />
                            </div>; 

    return (
        <>
            {errorBox}
            <Form onSubmit={ handleSignup } id="signupForm" method="post">
                <Form.Group controlId="email">
                    <Form.Label className={styles.login}>Email</Form.Label>
                    <Form.Control type="text" name="email"  
                        onChange={editEmail} 
                        onBlur={validateFieldOnBlur}
                        value={email} 
                        
                    />
                    <Form.Text className="helper" as="div">    
                    </Form.Text>
                </Form.Group>

                <Form.Group controlId="password">
                    <Form.Label className={styles.login}>Parolă</Form.Label>
                    <Form.Control type="password" name="password"  
                        onChange={editPassword} 
                        value={password} 
                        onBlur={validateFieldOnBlur}
                        
                    />
                    <Form.Text className="helper" as="div">    
                    </Form.Text>
                </Form.Group>

                <Form.Group controlId="passwordConfirmation">
                    <Form.Label className={styles.login}>Repetă Parola</Form.Label>
                    <Form.Control type="password" name="password_confirmation" 
                        onChange={editPasswordConfirmation} 
                        value={passwordConfirmation} 
                        onBlur={validateFieldOnBlur}
                        
                    />
                    <Form.Text className="helper" as="div">    
                    </Form.Text>
                </Form.Group>
                <Form.Group controlId="terms" className={styles.checkboxCustom} >
                    <Form.Check type="checkbox" label={termsLabel} className={styles.labelWrapper}
                        checked={isChecked}
                        onChange={handleChangeCheckbox }
                        value="accept"
                    />
                    <Form.Text className="helper" as="div">
                    </Form.Text>
                </Form.Group>
                <Form.Group controlId="newsletter" className={styles.checkboxCustom}>
                    <Form.Check
                        type="checkbox"
                        label="Vreau să primesc newsletter-ul Conso cu oferte și noutăți (opțional, poți dezactiva oricând)"
                        className={styles.labelWrapper}
                        checked={isNewsletter}
                        onChange={(e) => editIsNewsletter(e.target.checked)}
                    />
                </Form.Group>
                {signupBtn}

            </Form>
            {loginLink &&
                <>
                    <hr />
                    <p className="mt-3">
                        Ai deja un cont? <Link href="/login" title="Conectează-te la contul Conso">Conectează-te</Link>.
                    </p>
                </>
            }
        </>
    );
}

export default SignupForm;
